/**
 * GD Load More / Infinite scroll for collection product grids.
 *
 * Implemented as a custom element on purpose: Dawn's facet filtering replaces
 * #ProductGridContainer.innerHTML wholesale, which would wipe any inline script
 * and its listeners. A custom element is re-upgraded automatically on insertion,
 * so connectedCallback re-arms everything after every filter/sort — no changes
 * to facets.js required, and paging state resets correctly for the new results.
 *
 * Markup contract (rendered by GD-main-collection-product-grid.liquid):
 *   <gd-load-more data-mode="load_more|infinite" data-next-url="/collections/x?page=2">
 *     <button data-load-more-button>…</button>
 *     <div data-load-more-status role="status" aria-live="polite"></div>
 *     <div data-load-more-sentinel></div>
 *   </gd-load-more>
 */
class GDLoadMore extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById('product-grid');
    this.button = this.querySelector('[data-load-more-button]');
    this.status = this.querySelector('[data-load-more-status]');
    this.sentinel = this.querySelector('[data-load-more-sentinel]');
    this.isLoading = false;

    if (!this.grid || !this.button) return;

    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);

    if (this.dataset.mode === 'infinite') this.startObserving();
  }

  disconnectedCallback() {
    if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
    this.stopObserving();
  }

  startObserving() {
    // No IntersectionObserver (or no sentinel) → fall back to the button.
    if (!this.sentinel || !('IntersectionObserver' in window)) {
      this.revealButton();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext(false);
      },
      { rootMargin: '800px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  /**
   * IntersectionObserver only reports *changes* in intersection. If the sentinel
   * is still on screen after a batch is appended (tall viewport, short batch),
   * no further callback fires and loading stalls. Re-observing forces a fresh
   * evaluation against the current position, so batches chain until the sentinel
   * is finally pushed out of range.
   */
  rearmObserver() {
    if (!this.observer || !this.sentinel || !this.dataset.nextUrl) return;
    this.observer.unobserve(this.sentinel);
    this.observer.observe(this.sentinel);
  }

  revealButton() {
    this.classList.add('gd-load-more--manual');
    if (this.button) this.button.classList.remove('visually-hidden');
  }

  stopObserving() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  onClick() {
    this.loadNext(true);
  }

  async loadNext(userInitiated) {
    const nextUrl = this.dataset.nextUrl;
    if (!nextUrl || this.isLoading) return;

    this.isLoading = true;
    this.setBusy(true);

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error(`Request failed with ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newGrid = doc.getElementById('product-grid');
      if (!newGrid) throw new Error('No #product-grid in the fetched page');

      const fragment = document.createDocumentFragment();
      let firstNew = null;

      newGrid.querySelectorAll(':scope > li').forEach((item) => {
        // In-grid custom blocks are positioned by their index WITHIN a page, so
        // every fetched page repeats them. Keep only page 1's copies.
        if (item.hasAttribute('data-ingrid-placement')) return;
        const node = document.importNode(item, true);
        if (!firstNew) firstNew = node;
        fragment.appendChild(node);
      });

      const addedCount = fragment.childElementCount;
      this.grid.appendChild(fragment);

      // Re-arm Dawn's reveal-on-scroll for the appended cards (no-op when the
      // theme setting is off, since there will be no .scroll-trigger elements).
      if (typeof initializeScrollAnimationTrigger === 'function') {
        try {
          initializeScrollAnimationTrigger(this.grid);
        } catch (animationError) {
          console.warn('[gd-load-more] animation re-init skipped', animationError);
        }
      }

      // Advance the cursor using the page we just fetched.
      const nextMarker = doc.querySelector('gd-load-more');
      const followUpUrl = nextMarker && nextMarker.getAttribute('data-next-url');

      if (followUpUrl) {
        this.dataset.nextUrl = followUpUrl;
        this.announce(this.strings().added.replace('[count]', addedCount));
      } else {
        this.finish();
      }

      // Keyboard users who clicked the button should land on the first new card.
      if (userInitiated && firstNew) {
        firstNew.setAttribute('tabindex', '-1');
        firstNew.focus();
      }
    } catch (error) {
      console.error('[gd-load-more]', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.setBusy(false);
      this.rearmObserver();
    }
  }

  finish() {
    delete this.dataset.nextUrl;
    this.stopObserving();
    this.classList.add('gd-load-more--complete');
    if (this.button) this.button.hidden = true;
    if (this.sentinel) this.sentinel.hidden = true;
    this.announce(this.strings().complete);
  }

  showError() {
    // Leave data-next-url intact so the same page can be retried, and surface
    // the button so the shopper has a way to trigger that retry.
    this.stopObserving();
    this.revealButton();
    this.announce(this.strings().error);
  }

  setBusy(isBusy) {
    this.classList.toggle('gd-load-more--loading', isBusy);
    if (!this.button) return;
    this.button.classList.toggle('loading', isBusy);
    this.button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    if (isBusy) {
      this.button.setAttribute('disabled', 'disabled');
    } else {
      this.button.removeAttribute('disabled');
    }
  }

  announce(message) {
    if (this.status && message) this.status.textContent = message;
  }

  strings() {
    return {
      added: this.dataset.textAdded || '[count] more products loaded',
      complete: this.dataset.textComplete || 'All products loaded',
      error: this.dataset.textError || "Couldn't load more products. Please try again.",
    };
  }
}

if (!customElements.get('gd-load-more')) {
  customElements.define('gd-load-more', GDLoadMore);
}
